import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PublishArtifactDraft } from "../utils/agent-artifacts-utils"
import type {
  HostedAgentArtifact,
  HostedAgentArtifactKind,
} from "@/api/client/hosted-agents-artifacts"

import { BookmarkPlus, Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

import { useCreateHostedAgentArtifact } from "../hooks/use-hosted-agent-artifacts"
import {
  ARTIFACT_KIND_LABEL_KEYS,
  ARTIFACT_SUMMARY_MAX_LENGTH,
  ARTIFACT_TITLE_MAX_LENGTH,
  artifactPublishErrorKey,
  initialPublishKind,
  isPublishableFromReply,
  PUBLISH_DIALOG_KINDS,
  publishArtifactTitle,
} from "../utils/agent-artifacts-utils"

type PublishArtifactDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  agentSlug: string
  agentName: string
  /**
   * `draft.existing` is the library artifact the reply continues. When set,
   * the dialog defaults to publishing the next version of that family, with
   * an opt-out that creates a new family instead.
   */
  draft: PublishArtifactDraft | null
  onPublished?: (artifact: HostedAgentArtifact) => void
}

type FamilyChoice = "new_version" | "new_family"

/**
 * "Publish as artifact" dialog (B4). Pre-filled from the reply (or from the
 * agent's `artifact_candidate`), posts to the agent's artifact library and
 * invalidates the library query so the landing page picks it up.
 */
export function PublishArtifactDialog({
  open,
  onOpenChange,
  agentSlug,
  agentName,
  draft,
  onPublished,
}: PublishArtifactDialogProps) {
  const { t } = useTranslation()
  const seed = draft?.existing ?? null
  const createArtifact = useCreateHostedAgentArtifact(agentSlug)

  const [title, setTitle] = useState("")
  const [kind, setKind] = useState<HostedAgentArtifactKind>("markdown")
  const [summary, setSummary] = useState("")
  const [familyChoice, setFamilyChoice] = useState<FamilyChoice>("new_version")
  const [publishError, setPublishError] = useState<string | null>(null)

  // Re-seed the form each time the dialog opens with a new draft.
  useEffect(() => {
    if (!open || !draft) return
    // A new version keeps the family's title (#3207).
    setTitle(publishArtifactTitle(draft.title, seed, "new_version"))
    setKind(initialPublishKind(draft.kind))
    setSummary(draft.summary)
    setFamilyChoice("new_version")
    setPublishError(null)
  }, [open, draft, seed])

  const trimmedTitle = title.trim()
  const canPublish = !!draft && trimmedTitle.length > 0 && !createArtifact.isPending
  const useSeed = !!seed && familyChoice === "new_version"

  const handlePublish = async () => {
    if (!draft || !canPublish) return
    setPublishError(null)
    try {
      const artifact = await createArtifact.mutateAsync({
        title: trimmedTitle.slice(0, ARTIFACT_TITLE_MAX_LENGTH),
        kind,
        content: draft.content,
        summary: summary.trim().slice(0, ARTIFACT_SUMMARY_MAX_LENGTH) || undefined,
        tags: [],
        provenance: draft.provenance,
        parent_id: useSeed ? seed.artifactId : undefined,
      })
      toast.success(
        t("agents.artifacts.published", { title: artifact.title, version: artifact.version })
      )
      onPublished?.(artifact)
      onOpenChange(false)
    } catch (error) {
      // The dialog stays open on failure, and a modal overlay makes the toast
      // unreachable (and, measured, invisible) — so the reason is rendered
      // INSIDE the dialog, next to the button the user just pressed. The toast
      // stays for the case where the dialog is dismissed before it is read.
      const message = t(artifactPublishErrorKey(error))
      setPublishError(message)
      toast.error(message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookmarkPlus aria-hidden="true" className="size-5" />
            {t("agents.artifacts.dialog_title")}
          </DialogTitle>
          <DialogDescription>
            {t("agents.artifacts.dialog_description", { name: agentName })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4 py-1">
          <div className="flex flex-col gap-2">
            <Label htmlFor="publish-artifact-title">{t("agents.artifacts.title_label")}</Label>
            <Input
              id="publish-artifact-title"
              maxLength={ARTIFACT_TITLE_MAX_LENGTH}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="publish-artifact-kind">{t("agents.artifacts.kind_label")}</Label>
            <Select
              value={kind}
              onValueChange={(value) => setKind(value as HostedAgentArtifactKind)}
            >
              <SelectTrigger className="w-full" id="publish-artifact-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PUBLISH_DIALOG_KINDS.map((option) =>
                  isPublishableFromReply(option) ? (
                    <SelectItem key={option} value={option}>
                      {t(ARTIFACT_KIND_LABEL_KEYS[option])}
                    </SelectItem>
                  ) : (
                    // Listed so the reader sees the format exists, disabled
                    // because a reply carries no file the library could
                    // reference (the backend requires a `blob_ref`).
                    <SelectItem key={option} disabled value={option}>
                      {t("agents.artifacts.kind_unavailable_option", {
                        kind: t(ARTIFACT_KIND_LABEL_KEYS[option]),
                      })}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
            {draft && !isPublishableFromReply(draft.kind) ? (
              <p
                className="text-muted-foreground text-xs leading-snug"
                data-testid="publish-artifact-kind-note"
              >
                {t("agents.artifacts.document_publish_unavailable")}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="publish-artifact-summary">{t("agents.artifacts.summary_label")}</Label>
            <Textarea
              id="publish-artifact-summary"
              maxLength={ARTIFACT_SUMMARY_MAX_LENGTH}
              placeholder={t("agents.artifacts.summary_placeholder")}
              rows={3}
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
            />
          </div>

          {seed ? (
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-sm font-medium">
                {t("agents.artifacts.family_label")}
              </legend>
              <RadioGroup
                value={familyChoice}
                onValueChange={(value) => {
                  const choice = value as FamilyChoice
                  const derived = (from: FamilyChoice) =>
                    publishArtifactTitle(draft?.title ?? "", seed, from)
                  // Follow the choice unless the user has edited the title.
                  setTitle((prev) => (prev === derived(familyChoice) ? derived(choice) : prev))
                  setFamilyChoice(choice)
                }}
              >
                <div className="flex items-start gap-2">
                  <RadioGroupItem id="publish-artifact-new-version" value="new_version" />
                  <Label className="leading-snug" htmlFor="publish-artifact-new-version">
                    {t("agents.artifacts.new_version_option", {
                      title: seed.title,
                      version: seed.version + 1,
                    })}
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem id="publish-artifact-new-family" value="new_family" />
                  <Label className="leading-snug" htmlFor="publish-artifact-new-family">
                    {t("agents.artifacts.new_family_option")}
                  </Label>
                </div>
              </RadioGroup>
            </fieldset>
          ) : null}

          {publishError ? (
            <p
              className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm"
              data-testid="publish-artifact-error"
              role="alert"
            >
              {publishError}
            </p>
          ) : null}
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button
            disabled={createArtifact.isPending}
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("agents.artifacts.cancel")}
          </Button>
          <Button disabled={!canPublish} onClick={() => void handlePublish()}>
            {createArtifact.isPending ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : (
              <BookmarkPlus aria-hidden="true" className="size-4" />
            )}
            {createArtifact.isPending
              ? t("agents.artifacts.publishing")
              : t("agents.artifacts.publish")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
